import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../Datas/siteConfig";
import "../css/cartpage.scss";

const getProductById = (id) =>
  productData.find((product) => String(product.id) === String(id));

const scrollToTop = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  } = useContext(CartContext);

  const items = useMemo(
    () =>
      cartItems
        .map((item) => ({ ...item, product: getProductById(item.id) }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (total, item) => total + Number(item.product.price) * item.quantity,
        0
      ),
    [items]
  );
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const shipping = siteConfig.defaultShipping;

  const handleVariantChange = (item, field, nextValue) => {
    const nextSize = field === "size" ? nextValue : item.size;
    const nextColor = field === "color" ? nextValue : item.color;

    if (nextSize === item.size && nextColor === item.color) return;
    removeFromCart(item.id, item.size, item.color);
    addToCart(item.id, item.quantity, nextSize, nextColor);
  };

  const handleCheckout = () => {
    navigate("/checkout");
    scrollToTop();
  };

  if (items.length === 0) {
    return (
      <main className="cart-page cart-page--empty" id="main-content">
        <section className="cart-page__empty" aria-labelledby="empty-bag-title">
          <span className="cart-page__empty-mark" aria-hidden="true">
            0
          </span>
          <p className="cart-page__eyebrow">Your bag</p>
          <h1 id="empty-bag-title">Your bag is waiting for a little colour</h1>
          <p>
            Discover hand-painted pieces from {siteConfig.brandName}, made in
            small editions to live with you for years.
          </p>
          <Link to="/shop" onClick={scrollToTop}>
            Explore the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content" aria-labelledby="cart-title">
      <header className="cart-page__header">
        <p className="cart-page__eyebrow">Your selection</p>
        <h1 id="cart-title">Shopping bag</h1>
        <p>
          {itemCount} {itemCount === 1 ? "piece" : "pieces"}
        </p>
      </header>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Items in your bag">
          <div className="cart-page__shipping-progress">
            <p>
              Flat-rate tracked shipping: {formatPrice(shipping)}.
            </p>
            <div
              className="cart-page__progress-track"
              role="progressbar"
              aria-label="Flat-rate shipping"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow="100"
            >
              <span style={{ width: "100%" }} />
            </div>
          </div>

          <div className="cart-page__item-list">
            {items.map((item) => {
              const { product } = item;
              const image = product.images?.[0] || product.image;
              const sizes = product.sizes || product.size || [];
              const colors = product.colors || [];

              return (
                <article
                  className="cart-item"
                  key={`${item.id}-${item.size}-${item.color}`}
                >
                  <Link
                    className="cart-item__image"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    <img src={image} alt="" loading="lazy" decoding="async" />
                  </Link>

                  <div className="cart-item__body">
                    <div className="cart-item__heading">
                      <div>
                        <p>{product.category}</p>
                        <h2>
                          <Link to={`/shopDetails/${product.id}`}>
                            {product.name}
                          </Link>
                        </h2>
                      </div>
                      <strong>{formatPrice(product.price * item.quantity)}</strong>
                    </div>

                    <div className="cart-item__editors">
                      {colors.length > 0 && (
                        <label>
                          <span>Colour</span>
                          <select
                            value={item.color}
                            onChange={(event) =>
                              handleVariantChange(item, "color", event.target.value)
                            }
                            aria-label={`Colour for ${product.name}`}
                          >
                            {colors.map((color) => (
                              <option key={color} value={color}>
                                {color}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      {sizes.length > 0 && (
                        <label>
                          <span>Size</span>
                          <select
                            value={item.size}
                            onChange={(event) =>
                              handleVariantChange(item, "size", event.target.value)
                            }
                            aria-label={`Size for ${product.name}`}
                          >
                            {sizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>

                    <div className="cart-item__footer">
                      <div className="cart-item__quantity" aria-label={`Quantity for ${product.name}`}>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity - 1
                            )
                          }
                          disabled={item.quantity === 1}
                          aria-label={`Decrease ${product.name} quantity`}
                        >
                          −
                        </button>
                        <span aria-live="polite">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.id,
                              item.size,
                              item.color,
                              item.quantity + 1
                            )
                          }
                          aria-label={`Increase ${product.name} quantity`}
                        >
                          +
                        </button>
                      </div>

                      <button
                        type="button"
                        className="cart-item__remove"
                        onClick={() =>
                          removeFromCart(item.id, item.size, item.color)
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="cart-page__list-actions">
            <Link to="/shop" onClick={scrollToTop}>
              ← Continue shopping
            </Link>
            <button type="button" onClick={clearCart}>
              Clear bag
            </button>
          </div>
        </section>

        <aside className="cart-page__summary" aria-labelledby="order-summary-title">
          <div className="cart-page__summary-card">
            <h2 id="order-summary-title">Order summary</h2>

            <dl>
              <div>
                <dt>Subtotal</dt>
                <dd>{formatPrice(subtotal)}</dd>
              </div>
              <div>
                <dt>Shipping</dt>
                <dd>{formatPrice(shipping)}</dd>
              </div>
              <div className="cart-page__summary-total">
                <dt>Total</dt>
                <dd>{formatPrice(subtotal + shipping)}</dd>
              </div>
            </dl>

            <p>Taxes included. Any applicable duties are calculated at delivery.</p>

            <button type="button" onClick={handleCheckout}>
              Proceed to checkout
            </button>

            <div className="cart-page__assurances" aria-label="Shopping assurances">
              <span>Secure checkout</span>
              <span>{siteConfig.returnsWindowDays}-day returns</span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
